import React, { useState, useEffect, useRef, useContext } from "react";
import { getFirebaseUsers } from "../data/acceptedUsers";
import useRealtimePresence from "../utils/useRealtimePresence";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useUserNotifications } from "../utils/useUserNotifications";
import { getItemById } from "../utils/getItemById";
import client, { urlFor } from "../sanityClient";
import { RequestModalContext } from "../App";

function getInitials(user) {
  if (!user) return "?";
  if (user.name && user.name.length > 0) return user.name[0].toUpperCase();
  return "?";
}

const Header = ({ user }) => {
  const { setShowRequestModal } = useContext(RequestModalContext);
  const activeUsers = useRealtimePresence(user);
  const [firebaseUsers, setFirebaseUsers] = useState([]);
  const notifications = useUserNotifications(user?.email);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [itemData, setItemData] = useState({});
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch Firebase users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getFirebaseUsers();
      setFirebaseUsers(users);
    };
    fetchUsers();
  }, []);

  // Map Firebase users to show all, and mark if active
  const displayUsers = firebaseUsers
    .map((u) => {
      const isActive = activeUsers.some(
        (a) => a.email.toLowerCase() === u.email.toLowerCase()
      );
      return { ...u, isActive };
    })
    .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1));

  const activeCount = activeUsers.length;

  useEffect(() => {
    async function fetchItems() {
      const data = {};
      for (const notif of notifications) {
        if (!itemData[notif.itemId]) {
          data[notif.itemId] = await getItemById(notif.itemId);
        }
      }
      setItemData((prev) => ({ ...prev, ...data }));
    }
    if (notifications.length > 0) fetchItems();
    // eslint-disable-next-line
  }, [notifications]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="text-xl text-gray-800 hover:text-black transition-colors flex items-center cursor-pointer"
            >
              @titledworks
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <button
              onClick={() => setShowRequestModal(true)}
              className="hidden sm:block text-sm text-gray-500 hover:text-gray-900"
            >
              Submit a request
            </button>
            <div className="relative flex items-center" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="relative"
              >
                <span role="img" aria-label="Notifications">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V16H8.56C9.25 17.19 10.53 18 12.01 18C13.49 18 14.76 17.19 15.46 16H19V19ZM19 14H14.01C14.01 15.1 13.11 16 12.01 16C10.91 16 10.01 15.1 10.01 14H5V5H19V14Z"
                      fill="black"
                    />
                  </svg>
                </span>
                {notifications.length > 0 && (
                  <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-[100%] mt-2 w-80 bg-white shadow-lg rounded-lg z-50">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-gray-500">No new replies</div>
                  ) : (
                    <div>
                      {notifications.map((notif) => {
                        const item = itemData[notif.itemId];
                        const imageUrl = item?.images?.[0]
                          ? urlFor(item.images[0]).width(100).url()
                          : null;
                        return (
                          <div
                            key={notif.id}
                            className="flex items-center p-2 border-b cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                              navigate(`/item/${notif.itemId}#offer`);
                              setDropdownOpen(false);
                            }}
                          >
                            {imageUrl && (
                              <img
                                src={imageUrl}
                                className="w-12 h-12 rounded mr-2"
                                alt={item?.designer}
                              />
                            )}
                            <div>
                              <div>{item?.designer}</div>
                              <div className="text-xs text-gray-500">
                                {item?.year} {item?.season}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center">
              {displayUsers.map((u, i) => (
                <div
                  key={u.email + i}
                  className={`relative group${i !== 0 ? " -ml-2" : ""}`}
                  style={{ zIndex: 10 - i }}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white cursor-pointer
                      ${
                        u.isActive
                          ? "bg-black text-black border-black"
                          : "bg-gray-100 text-gray-300 border-gray-100"
                      }`}
                  >
                    {getInitials(u)}
                  </div>
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 z-30 hidden group-hover:block">
                    <div className="bg-black text-white text-xs rounded px-3 py-2 shadow-lg whitespace-nowrap">
                      <div>{u.name || u.email}</div>
                      {u.isActive && (
                        <div className="text-green-400 mt-1">Active</div>
                      )}
                      {!u.isActive && u.lastSeen && (
                        <div className="text-gray-400 mt-1 text-xs">
                          Last seen{" "}
                          {formatDistanceToNow(new Date(u.lastSeen), {
                            addSuffix: true,
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* {activeCount > 0 && (
        <div className="fixed bottom-4 right-4 z-50 bg-black text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium">
          chat bot?
        </div>
      )} */}
    </header>
  );
};

export default Header;
